import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { BOTTOM_TABS, BOTTOM_ADD_ACTIONS } from "./navConfig";
import { X } from "lucide-react";

/**
 * Bottom-tab navigation for mobile (< md breakpoint).
 * Slots: Home · Action · Add · Reports · More
 *  - Add: opens a bottom sheet of role-filtered quick actions.
 *  - More: opens the main sidebar drawer via `onOpenMenu()`.
 */
export default function BottomNav({ onOpenMenu }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [addOpen, setAddOpen] = useState(false);

    // Close the sheet on route change or ESC
    useEffect(() => {
        if (!addOpen) return undefined;
        const onKey = (e) => e.key === "Escape" && setAddOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [addOpen]);

    const canSee = (roles) => !roles || roles.includes(user?.role);
    const addActions = BOTTOM_ADD_ACTIONS.filter((a) => canSee(a.roles));

    const handleTap = (t) => {
        if (t.kind === "sheet") { setAddOpen(true); return; }
        if (t.kind === "drawer") { onOpenMenu(); return; }
        navigate(t.to);
    };

    return (
        <>
            <nav
                className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white/95 backdrop-blur border-t border-stone-200"
                style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
                data-testid="bottom-nav"
            >
                <div className="grid grid-cols-5 h-14">
                    {BOTTOM_TABS.map((t) => t.to ? (
                        <NavLink
                            key={t.id}
                            to={t.to}
                            end={t.exact}
                            data-testid={`botnav-${t.id}`}
                            className={({ isActive }) =>
                                `flex flex-col items-center justify-center gap-0.5 text-[10px] transition-colors ${
                                    isActive ? "text-stone-900 font-medium" : "text-stone-500"
                                }`
                            }
                        >
                            <t.icon size={18} strokeWidth={1.8} />
                            <span>{t.label}</span>
                        </NavLink>
                    ) : (
                        <button
                            key={t.id}
                            onClick={() => handleTap(t)}
                            data-testid={`botnav-${t.id}`}
                            className={`flex flex-col items-center justify-center gap-0.5 text-[10px] transition-colors ${
                                t.id === "add"
                                    ? "text-amber-700"
                                    : "text-stone-500 hover:text-stone-900"
                            }`}
                            aria-label={t.label}
                        >
                            {t.id === "add" ? (
                                <span className="w-9 h-9 -mt-3 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-lg border-4 border-white">
                                    <t.icon size={18} strokeWidth={2.2} />
                                </span>
                            ) : (
                                <t.icon size={18} strokeWidth={1.8} />
                            )}
                            <span>{t.label}</span>
                        </button>
                    ))}
                </div>
            </nav>

            {/* ADD bottom sheet */}
            {addOpen && (
                <div
                    className="fixed inset-0 z-50 md:hidden bg-black/40"
                    onClick={() => setAddOpen(false)}
                    data-testid="botnav-add-sheet"
                >
                    <div
                        className="absolute bottom-0 inset-x-0 bg-white rounded-t-2xl shadow-2xl p-4 pb-8"
                        style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-[10px] font-semibold tracking-widest text-stone-500 uppercase">Quick add</div>
                                <div className="text-lg font-semibold text-stone-900">What are you logging?</div>
                            </div>
                            <button
                                onClick={() => setAddOpen(false)}
                                className="w-8 h-8 rounded-full border border-stone-200 flex items-center justify-center"
                                data-testid="botnav-add-close"
                                aria-label="Close"
                            >
                                <X size={14} />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {addActions.length === 0 ? (
                                <div className="col-span-2 py-6 text-center text-sm text-stone-500">
                                    No quick actions available for your role.
                                </div>
                            ) : addActions.map((a) => (
                                <button
                                    key={a.to}
                                    onClick={() => { setAddOpen(false); navigate(a.to); }}
                                    data-testid={`botnav-add-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="flex items-center gap-2.5 p-3 rounded-xl border border-stone-200 hover:border-amber-400 hover:bg-amber-50/50 active:bg-amber-100 transition-colors text-left"
                                >
                                    <span className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
                                        <a.icon size={16} strokeWidth={1.8} className="text-stone-700" />
                                    </span>
                                    <span className="text-sm font-medium text-stone-900">{a.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
